import { useEffect, useMemo } from 'react';
import { useToast } from '@cad-arc';
import type { FieldError, FieldErrors, UseFormReturn } from 'react-hook-form';
import type { EventPanelFormType, AttributeOption, CallForServiceOption } from '@mark43/cad-api';
import { useIntegratedForm } from '~/forms/use-integrated-form';
import { useBootstrapState } from '~/bootstrap/bootstrap-provider';

type DraftMeta =
    | {
          callForServiceId?: number;
          riskLevelAttributeId?: number;
          priorityAttributeId?: number;
      }
    | null
    | undefined;

type Params = {
    eventId?: number;
    eventForm: UseFormReturn<EventPanelFormType>;
    eventPanelOptions?: {
        riskLevelOptions?: AttributeOption[];
        priorityOptions?: AttributeOption[];
    };
    callForServiceOptions?: CallForServiceOption[];
    eventDraftMetaData?: DraftMeta;
    currentProQaTempId?: string;
    eventDraftProQaTempId?: string;
};

type MappingEntry = {
    entity: 'callForService' | 'riskLevel' | 'priority';
    id: number | null;
    options: (AttributeOption | CallForServiceOption)[];
};

const PRO_QA_DRAFT_AUTOSAVE_REQUIRED_FIELDS_MESSAGE =
    'ProQA matched a Call For Service, but event could not be auto-saved because required fields are missing. Fill the required fields and save manually.';

export function useEventDraftProQaMetadataSync({
    eventId,
    eventForm,
    eventPanelOptions,
    callForServiceOptions,
    eventDraftMetaData,
    currentProQaTempId,
    eventDraftProQaTempId,
}: Params): void {
    const { onSubmit } = useIntegratedForm<EventPanelFormType>();
    const { eventPanelConfiguration } = useBootstrapState();
    const toast = useToast();

    const mappings: MappingEntry[] = useMemo(() => {
        if (
            eventId ||
            !eventPanelOptions ||
            !callForServiceOptions ||
            currentProQaTempId !== eventDraftProQaTempId
        ) {
            return [];
        }

        return [
            {
                entity: 'callForService',
                id: eventDraftMetaData?.callForServiceId ?? null,
                options: callForServiceOptions ?? [],
            },
            {
                entity: 'riskLevel',
                id: eventDraftMetaData?.riskLevelAttributeId ?? null,
                options: eventPanelOptions?.riskLevelOptions ?? [],
            },
            {
                entity: 'priority',
                id: eventDraftMetaData?.priorityAttributeId ?? null,
                options: eventPanelOptions?.priorityOptions ?? [],
            },
        ];
    }, [
        eventId,
        eventDraftMetaData?.callForServiceId,
        eventDraftMetaData?.riskLevelAttributeId,
        eventDraftMetaData?.priorityAttributeId,
        callForServiceOptions,
        eventPanelOptions,
        currentProQaTempId,
        eventDraftProQaTempId,
    ]);

    useEffect(() => {
        if (mappings.length === 0) {
            return;
        }

        let hasSetValue = false;
        let timeoutId: NodeJS.Timeout | undefined;

        for (const { entity, id, options } of mappings) {
            if (!id || !options?.length || eventForm.formState?.dirtyFields?.[entity]) {
                continue;
            }

            const entityFormField = options.find((o) => o?.value === id);
            if (!entityFormField) {
                continue;
            }

            const curr = eventForm.getValues(entity);
            if (curr?.value === entityFormField.value) {
                continue;
            }

            eventForm.setValue(entity, entityFormField, { shouldDirty: false, shouldTouch: false });
            hasSetValue = true;
        }

        if (hasSetValue) {
            timeoutId = setTimeout(async () => {
                const emergencyCommunication = eventPanelConfiguration?.eventCommunicationEmergency;
                if (emergencyCommunication) {
                    eventForm.setValue('eventCommunication', emergencyCommunication);
                }
                const passedValidation = await eventForm.trigger(undefined, {
                    shouldFocus: false,
                });
                if (!passedValidation) {
                    if (hasRequiredValidationErrors(eventForm.formState.errors)) {
                        toast({
                            status: 'error',
                            description: PRO_QA_DRAFT_AUTOSAVE_REQUIRED_FIELDS_MESSAGE,
                        });
                    }
                    return;
                }
                onSubmit(undefined);
            }, 0);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [mappings, eventForm, onSubmit, eventPanelConfiguration, eventId, toast]);
}

function hasRequiredValidationErrors(errors: FieldErrors<EventPanelFormType> | undefined): boolean {
    if (!errors) {
        return false;
    }
    return Object.values(errors).some((value) => {
        if (!value) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.some((entry) =>
                typeof entry === 'object' && entry !== null
                    ? hasRequiredValidationErrors(entry as FieldErrors<EventPanelFormType>)
                    : false
            );
        }
        if (isFieldError(value)) {
            return value.message === 'Required';
        }
        if (typeof value === 'object') {
            return hasRequiredValidationErrors(value as FieldErrors<EventPanelFormType>);
        }
        return false;
    });
}

function isFieldError(value: unknown): value is FieldError {
    return typeof value === 'object' && value !== null && 'type' in value;
}
